import {createSlice} from "@reduxjs/toolkit";
import {addVectors, getNewCoordinates, headingDiff, inverseHeading} from "../assets/virtual-flyout/nav-calc.js";
import {
    calculateChange,
    getAcceleration,
    getEmptyWeight,
    getFuelFlow,
    getFuelWeight,
    getPayloadWeight,
    getPower,
    getTas,
    getVS,
    getVx,
    getVy
} from "../assets/virtual-flyout/perf-calc.js";
import {getTemperature, getWind} from "../assets/virtual-flyout/weather-service.js";

export const initialState = {
    heading: 0,
    track: 0,
    altitude: 0,
    power: 0,
    fuel: 0,
    tas: 0,
    gs: 0,
    airborn: false,
    distress: false,
    roll: 0, // -1 left, 0 level, 1 right
    pitch: 0, // -1 descend, 0 level, 1 climb
    vs: 0,
    target: {
        power: null,
        tas: null,
        altitude: null,
        heading: null,
    },
    coordinates: {lat: 0, long: 0},
    type: null,
    pob: 0,
    weight: {
        empty: 0,
        fuel: 0,
        payload: 0
    },
    wind: {heading: 0, speed: 0},
    temperature: 15,
    conditions: [],
    trackLog: []
}

export const CONDITION_TYPES = {
    OUT_OF_FUEL: "OUT_OF_FUEL",
    MTOW_EXCEEDED: "MTOW_EXCEEDED",
    TORR_EXCEEDED: "TORR_EXCEEDED",
    TODR_EXCEEDED: "TODR_EXCEEDED", 
    LDR_EXCEEDED: "LDR_EXCEEDED"
}

function updateFuel(state, fuel) {
    const fuelWeight = getFuelWeight(fuel);
    state.fuel = fuel;
    state.weight.fuel = fuelWeight;
}

export const planeSlice = createSlice({
    name: 'planes',
    initialState: {},  // Will store planes by their IDs
    reducers: {
        moveTo(state, action) {
            const { planeId, coordinates, altitude } = action.payload;
            const plane = state[planeId];
            if (plane) {
                plane.coordinates = coordinates;
                plane.altitude = altitude;
                plane.temperature = getTemperature(altitude);
                plane.wind = getWind();
            }
        },
        refuel(state, action) {
            const { planeId, fuel } = action.payload;
            const plane = state[planeId];
            if (plane) {
                updateFuel(plane, fuel);
            }
        },
        lineUp(state, action) {
            const { planeId, heading } = action.payload;
            const plane = state[planeId];
            if (plane) {
                plane.heading = heading;
                plane.target.heading = heading;
            }
        },
        setPower(state, action) {
            const { planeId, power } = action.payload;
            const plane = state[planeId];
            if (plane) {
                plane.power = power;
                plane.target.power = power;
                plane.target.tas = null;
            }
        },
        setTas(state, action) {
            const { planeId, tas } = action.payload;
            const plane = state[planeId];
            if (plane) {
                plane.target.tas = tas;
                plane.target.power = null;
            }
        },
        setAirborn(state, action) {
            const { planeId, airborn } = action.payload;
            const plane = state[planeId];
            if (plane) {
                plane.airborn = airborn;
            }
        },
        turn(state, action) {
            const { planeId, heading, roll } = action.payload;
            const plane = state[planeId];
            if (plane) {
                plane.target.heading = heading;
                if (roll === null || roll === undefined) {
                    plane.roll = Math.sign(headingDiff(plane.heading, heading));
                } else {
                    plane.roll = roll;
                }
            }
        },
        climbVx(state, action) {
            const { planeId, altitude } = action.payload;
            const plane = state[planeId];
            if (plane) {
                plane.target.altitude = altitude;
                plane.target.tas = getVx();
                plane.pitch = 1;
            }
        },
        climbVy(state, action) {
            const { planeId, altitude } = action.payload;
            const plane = state[planeId];
            if (plane) {
                plane.target.altitude = altitude;
                plane.target.tas = getVy();
                plane.pitch = 1;
            }
        },
        climbCruise(state, action) {
            const { planeId, altitude } = action.payload;
            const plane = state[planeId];
            if (plane) {
                plane.target.altitude = altitude;
                plane.target.tas = plane.tas;   // maintain speed
                plane.pitch = 1;
            }
        },
        descend(state, action) {
            const { planeId, altitude } = action.payload;
            const plane = state[planeId];
            if (plane) {
                plane.target.altitude = altitude;
                plane.target.tas = plane.tas;   // maintain speed
                plane.pitch = -1;
            }
        },
        setCondition(state, action) {
            const { planeId, condition } = action.payload;
            const plane = state[planeId];
            if (plane && !plane.conditions.includes(condition)) {
                plane.conditions.push(condition);
                if ([
                    CONDITION_TYPES.LDR_EXCEEDED, 
                    CONDITION_TYPES.TORR_EXCEEDED, 
                    CONDITION_TYPES.TODR_EXCEEDED,
                    CONDITION_TYPES.MTOW_EXCEEDED
                ].includes(condition)) {
                    plane.distress = true;
                }
            }
        },
        addPlane(state, action) {
            const { planeId, type, pob } = action.payload;
            state[planeId] = {
                ...initialState,
                type: type,
                pob: pob,
                weight: {
                    empty: getEmptyWeight(type),
                    fuel: 0,
                    payload: getPayloadWeight(pob)
                }
            };
        },
        removePlane(state, action) {
            const { planeId } = action.payload;
            delete state[planeId];
        },
        tick(state, action) {
            const { planeId, timePassed } = action.payload;
            const plane = state[planeId];
            
            let speedUpdated = false;
            let headingUpdated = false;

            // update power if maintaining target speed
            if (plane.target.tas !== null) {
                plane.power = getPower(plane.target.tas, plane.altitude, plane.pitch);
            }

            // update fuel if power is on
            if (plane.power > 0) {
                updateFuel(plane, plane.fuel + calculateChange(plane.fuel, 0, -getFuelFlow(plane.type, plane.power),
                    timePassed / (1000 * 3600)));
                if (plane.fuel === 0) {
                    if (!plane.conditions.includes(CONDITION_TYPES.OUT_OF_FUEL)) {
                        plane.conditions.push(CONDITION_TYPES.OUT_OF_FUEL);
                        plane.distress = true;
                    }
                }
            }

            // update speed if not maintaining target speed
            if ((plane.target.tas === null) || (plane.tas !== plane.target.tas)) {
                const targetTas = plane.target.tas ?? getTas(plane.type, plane.temperature, plane.altitude, plane.power, plane.airborn);
                plane.tas += calculateChange(
                    plane.tas,
                    targetTas,
                    getAcceleration() * Math.sign(targetTas - plane.tas),
                    timePassed / 1000
                );
                speedUpdated = true;
            }

            // update heading if turning
            if (plane.target.heading !== null && plane.heading !== plane.target.heading) {
                plane.heading += calculateChange(plane.heading, plane.target.heading,
                    plane.roll * 3, timePassed / 1000, 360)
                plane.heading = (plane.heading + 360) % 360;
                headingUpdated = true;
            }

            // apply wind if not on the ground
            if (speedUpdated || headingUpdated) {
                if (plane.airborn) {
                    const planeVector = {heading: plane.heading, speed: plane.tas};
                    const windVector = {heading: inverseHeading(plane.wind.heading), speed: plane.wind.speed};
                    const groundVector = addVectors(planeVector, windVector);
                    plane.track = groundVector.heading;
                    plane.gs = groundVector.speed;
                } else {
                    plane.gs = plane.tas;
                    plane.track = plane.heading;
                }
            }

            // update Vs and altitude if pitching up or down
            if (plane.pitch !== 0 && plane.target.altitude !== null && plane.altitude !== plane.target.altitude) {
                const weight = plane.weight.empty + plane.weight.fuel + plane.weight.payload;
                plane.vs = getVS(plane.type, plane.temperature, plane.altitude, weight, plane.pitch, plane.power);
                plane.altitude += calculateChange(plane.altitude, plane.target.altitude,
                    plane.vs, timePassed / 60000);
                plane.temperature = getTemperature(plane.altitude);
                if (plane.altitude === plane.target.altitude) { // level off
                    if (plane.fuel > 0) {
                        plane.power = 75;
                    } else {
                        plane.airborn = false;
                    }
                    plane.pitch = 0;
                    plane.vs = 0;
                    plane.target.tas = null;
                }
            }

            // update coordinates if moving
            if (plane.gs > 0) {
                const distance = plane.gs * timePassed / 3600000;
                plane.coordinates = getNewCoordinates(plane.coordinates, distance, plane.track);
                plane.trackLog.push({lat: plane.coordinates.lat, long: plane.coordinates.long});
            }

            // update variables dependent on conditions
            if (plane.conditions.includes(CONDITION_TYPES.OUT_OF_FUEL)) {
                plane.power = 0;
                plane.target.tas = null;
                plane.pitch = -1;
                plane.target.altitude = 0;
            }
        },
        reset(state, action) {
            const { planeId, type, pob } = action.payload;
            state[planeId] = {
                ...initialState,
                type: type,
                pob: pob,
                weight: {
                    empty: getEmptyWeight(type),
                    fuel: 0,
                    payload: getPayloadWeight(pob)
                }
            };
        }
    }
});

export const {
    addPlane, removePlane, moveTo, refuel, lineUp, setAirborn, turn,
    climbVx, climbVy, climbCruise,
    descend, setPower, setTas, tick, setCondition, reset
} = planeSlice.actions;

export const selectPlane = (state, planeId) => state.planes[planeId];
export const selectAllPlanes = state => state.planes;

export default planeSlice.reducer;
